/**
 * The sorting grammar shared by every ruled list, with no React attached.
 *
 * One encoding and one click cycle, kept here rather than in the component so
 * they can be read and tested without a DOM or a router.
 *
 * ## The encoding
 *
 * A sort is one string: `name` ascending, `-name` descending. `null` means "no
 * explicit sort", which resolves to the table's own declared default and never
 * to an unordered list. Every table here declares a default, so there is no such
 * thing as an unsorted one, and "cleared" means "back to the default".
 *
 * ## The cycle has an exit
 *
 * A first click sorts by the column's natural direction, a second flips it, and
 * a third clears back to the default. That third state is the point. Without it
 * there is no way back to a table's own ordering short of a reload, so a reader
 * who sorts by Guardian to answer one question is stuck with that order for the
 * rest of the visit.
 */

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  id: string;
  direction: SortDirection;
}

/** What a column offers up to be compared. */
export type Sortable = string | number | null | undefined;

/** Encodes a state back to its parameter form. `null` in, `null` out. */
export function serializeSort(state: SortState | null): string | null {
  if (!state) return null;
  return state.direction === 'desc' ? `-${state.id}` : state.id;
}

/**
 * Decodes a sort parameter, or `null` when it names nothing usable.
 *
 * `ids` is the table's own column list and is checked rather than trusted: the
 * parameter sits in the address bar where anyone can edit it, and an unknown id
 * would otherwise reach a comparator that has no accessor for it, quietly
 * leaving the rows in their incoming order while a header claimed to be sorted.
 * A bare `-`, an empty string and an unknown column all decode to `null`, which
 * the caller resolves to the default.
 */
export function parseSort(raw: string | undefined, ids: readonly string[]): SortState | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const direction: SortDirection = trimmed.startsWith('-') ? 'desc' : 'asc';
  const id = (direction === 'desc' ? trimmed.slice(1) : trimmed).trim();
  if (!id) return null;
  return ids.includes(id) ? { id, direction } : null;
}

/**
 * Advances the sort for a clicked column.
 *
 * A new column opens at `first`, the active column flips, and a second flip
 * clears (`null`, meaning the table's default).
 *
 * `first` differs by column type because a single answer is wrong half the
 * time. A count or a date wants its largest value first, so "most" and "newest"
 * are one click away. A name wants A to Z, and opening it descending would sort
 * Z to A, which nobody asks for.
 *
 * A table whose default sort IS the clicked column reads as a two state toggle,
 * because its third click clears to a default that is that same column and
 * direction. That is the honest consequence of "clear means default" rather than
 * a case worth special-casing, and every other column still gets all three.
 */
export function cycleSort(
  current: SortState | null,
  id: string,
  first: SortDirection,
): SortState | null {
  if (!current || current.id !== id) return { id, direction: first };
  if (current.direction === first) {
    return { id, direction: first === 'desc' ? 'asc' : 'desc' };
  }
  return null;
}

/**
 * Orders rows by one accessor.
 *
 * Two behaviours worth knowing about:
 *
 * Blanks sort last in BOTH directions. A student with no guardian recorded is
 * missing a value rather than holding a small one, so reversing the column
 * should not march every gap to the top and bury the rows that answer the
 * question. `null`, `undefined` and the empty string are all treated as blank,
 * because a cell rendering "-" is empty whichever of the three produced it.
 *
 * Ties keep their incoming order, because the sort is stable. That incoming
 * order is the server's, which is usually surname then forename, so sorting by
 * a coarse column like Role leaves each group alphabetical rather than
 * shuffled.
 */
export function sortRows<Row>(
  rows: readonly Row[],
  value: (row: Row) => Sortable,
  direction: SortDirection,
): Row[] {
  const flip = direction === 'desc' ? -1 : 1;
  const blank = (v: Sortable) => v === null || v === undefined || v === '';

  return rows.toSorted((left, right) => {
    const a = value(left);
    const b = value(right);

    // Outside the flip, so a blank stays at the bottom either way.
    if (blank(a) || blank(b)) {
      if (blank(a) && blank(b)) return 0;
      return blank(a) ? 1 : -1;
    }

    if (typeof a === 'number' && typeof b === 'number') return (a - b) * flip;

    // `numeric` so "Period 2" precedes "Period 10" rather than following it.
    return String(a).localeCompare(String(b), undefined, { numeric: true }) * flip;
  });
}
